import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OrdersAPI, ExternalServicesAPI, UsersAPI } from '../api';
import { Package, Server, FileText, Shield, Globe, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { useAdmin } from '../lib/hooks';
import { SafeHtml } from './SafeHtml';

function ExpandableNote({ note }: { note: string | null }) {
  const [expanded, setExpanded] = useState(false);
  
  const hasContent = note && note.replace(/<[^>]*>?/gm, '').trim().length > 0;

  return (
    <div className="pt-3 border-t border-gray-100 dark:border-slate-700">
      {hasContent ? (
        <button 
          onClick={() => setExpanded(!expanded)}
          className="flex items-start justify-between w-full text-left group"
        >
          <SafeHtml
            html={note}
            mode="rich"
            className={`text-sm text-gray-600 dark:text-gray-400 prose prose-sm dark:prose-invert max-w-none pr-2 flex-1 ${expanded ? '' : 'line-clamp-1 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0'}`}
          />
          <div className="text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0 mt-0.5">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>
      ) : (
        <div className="h-5 w-full"></div>
      )}
    </div>
  );
}

export function AdminUserDashboardPreview() {
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { clerkId } = useParams();
  const navigate = useNavigate();

  const [targetUser, setTargetUser] = useState<any | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [externalServices, setExternalServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAll() {
      if (!clerkId) {
        setError('Falta el identificador del usuario');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [userRes, ordersRes, externalRes] = await Promise.all([
          UsersAPI.getByClerkId(clerkId),
          OrdersAPI.listByUser(clerkId),
          ExternalServicesAPI.listByUser(clerkId),
        ]);

        if (userRes.success && userRes.data) {
          setTargetUser(userRes.data);
        } else {
          setError(userRes.error || 'No se pudo cargar el usuario');
        }

        if (ordersRes.success && ordersRes.data) {
          setOrders(ordersRes.data);
        }

        if (externalRes.success && externalRes.data) {
          setExternalServices(externalRes.data);
        }
      } catch (e) {
        console.error(e);
        setError('Error al cargar los datos del usuario');
      } finally {
        setLoading(false);
      }
    }

    if (isAdmin) {
      fetchAll();
    }
  }, [clerkId, isAdmin]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getNextPaymentDate = (createdAt: string, payType: string) => {
    if (!createdAt || !payType) return null;

    const date = new Date(createdAt);
    const now = new Date();

    while (date <= now) {
      if (payType === 'MENSUAL') {
        date.setMonth(date.getMonth() + 1);
      } else if (payType === 'ANUAL') {
        date.setFullYear(date.getFullYear() + 1);
      } else {
        return null;
      }
    }

    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'PAID':
        return 'bg-green-100 text-green-800';
      case 'ACTIVE':
        return 'bg-blue-100 text-blue-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente de Pago';
      case 'PAID':
        return 'Pagado';
      case 'ACTIVE':
        return 'Activo';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  if (adminLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center dark:bg-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-300">Cargando vista del usuario...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center dark:bg-slate-950">
        <div className="text-center">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-white">Acceso restringido</h2>
          <p className="text-gray-600 dark:text-gray-300">Solo administradores pueden ver esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 pb-20 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/admin/users')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a Usuarios
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8 dark:bg-slate-800 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Vista de Dashboard del Cliente</h1>
              <p className="text-gray-600 mt-1 dark:text-gray-300">
                Estás viendo el dashboard como:{' '}
                <span className="font-semibold">
                  {targetUser?.email || clerkId}
                </span>
              </p>
              {targetUser && (
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                  {targetUser.firstName} {targetUser.lastName}
                </p>
              )}
            </div>
            <div className="px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Solo lectura – vista de administrador
            </div>
          </div>
          {error && (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md border border-red-200 dark:bg-red-900/20 dark:border-red-900 dark:text-red-200">
              {error}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Servicios contratados</h2>
          <p className="text-xs text-red-600 dark:text-red-400 font-medium">
            Esta vista es solo para verificar cómo ve el usuario sus servicios.
          </p>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center dark:bg-slate-800 dark:border-slate-700">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4 dark:text-gray-600" />
            <h3 className="text-xl font-medium text-gray-900 mb-2 dark:text-white">Sin servicios activos</h3>
            <p className="text-gray-500 mb-6 dark:text-gray-400">
              Este usuario aún no tiene órdenes registradas.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orders.map((order) => {
              const svc = order.hosting || order.complement;
              const svcName = svc?.name || svc?.displayName || 'Servicio';
              const svcImage = svc?.mainPhotoUrl;
              const svcCurrency = svc?.currency;
              const quantity = order.quantity || 1;
              const svcPrice = svc ? (svc.price * quantity).toFixed(2) : '0.00';
              const svcPayType = svc?.payType || svc?.payTypeDisplay;

              return (
                <div
                  key={order.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden dark:bg-slate-800 dark:border-slate-700 flex flex-col h-full"
                >
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden dark:bg-slate-700">
                        {svcImage ? (
                          <img
                            src={svcImage}
                            alt={svcName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Server className="h-6 w-6 text-blue-600" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            order.status,
                          )}`}
                        >
                          {getStatusLabel(order.status)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900 mb-1 dark:text-white">
                        {svcName} {quantity > 1 ? `(x${quantity})` : ''}
                      </h3>
                      {order.notes && (
                        <span title="Esta orden tiene notas">
                          <FileText className="w-4 h-4 text-blue-400 mb-1 flex-shrink-0" />
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">{svcPayType}</p>

                    <div className="border-t border-gray-100 pt-4 space-y-3 dark:border-slate-700 mt-auto">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Dominio</span>
                        <span
                          className={`font-medium ${
                            order.domain
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-400 italic dark:text-gray-500'
                          }`}
                        >
                          {order.domain || 'dominio.com'}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Precio</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {svcCurrency} {svcPrice}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Fecha de Orden</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatDate(order.createdAt)}
                        </span>
                      </div>
                      {getNextPaymentDate(order.createdAt, svcPayType) && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 dark:text-gray-400">Próximo Pago</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {getNextPaymentDate(order.createdAt, svcPayType)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Último medio de pago</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {order.lastPaymentMethod
                            ? order.lastPaymentMethod === 'TRANSFERENCIA'
                              ? 'Transferencia'
                              : order.lastPaymentMethod.charAt(0) +
                                order.lastPaymentMethod.slice(1).toLowerCase()
                            : 'No definido'}
                        </span>
                      </div>
                      
                      <ExpandableNote note={order.notes} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {externalServices.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 dark:text-white">Servicios Externos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {externalServices.map((service) => (
                <div
                  key={service.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow dark:bg-slate-800 dark:border-slate-700 flex flex-col h-full"
                >
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden dark:bg-slate-700">
                        <Globe className="h-6 w-6 text-purple-600" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Externo
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-900 mb-1 dark:text-white">
                        {service.name}
                      </h3>
                    </div>
                    {service.type && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1 font-medium">
                        {service.type}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mb-4 dark:text-gray-400">
                      {service.payType === 'ANUAL' ? 'Anual' : 'Mensual'}
                    </p>

                    <div className="border-t border-gray-100 pt-4 space-y-3 dark:border-slate-700 mt-auto">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Dominio</span>
                        <span
                          className={`font-medium ${
                            service.domain
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-400 italic dark:text-gray-500'
                          }`}
                        >
                          {service.domain || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Precio</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          USD {service.price.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Inicio</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatDate(service.startDate)}
                        </span>
                      </div>
                      {service.nextPaymentDate && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500 dark:text-gray-400">Próximo Pago</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatDate(service.nextPaymentDate)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUserDashboardPreview;

